import { useState } from "react"
import Grid from "@mui/material/Grid"
import Icon from "@mui/material/Icon"
import DashboardLayout from "examples/LayoutContainers/DashboardLayout"
import MDBox from "components/MDBox"
import MDButton from "components/MDButton"
import MDTypography from "components/MDTypography"
import ReportHeader from "./components/report/ReportHeader"
import ReportFilterToolbar from "./components/report/ReportFilterToolbar"
import ReportTabs from "./components/report/ReportTabs"
import ReportKpiCard from "./components/report/ReportKpiCard"
import ReportCard from "./components/report/ReportCard"
import ReportBarList from "./components/report/ReportBarList"
import { sortByValueDesc, topContributorSummary } from "./components/report/reportUtils"
import BulletBar from "./components/BulletBar"
import useProcurementFilters from "./hooks/useProcurementFilters"
import { useGetProcurementReportQuery } from "shared/redux/endpoints/procurementV2"
import { formatVND, formatAmount } from "./constants"

const FILTER_DEFAULTS = {
  search: "",
  region: "",
  center: "",
  category: "",
  department: "",
  from: "",
  to: "",
}

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "category", label: "By Category" },
  { key: "region", label: "By Region" },
  { key: "center", label: "By Center" },
  { key: "department", label: "By Department" },
  { key: "supplier", label: "By Supplier" },
  { key: "time", label: "By Time" },
]

const downloadCsv = (rows) => {
  const header = [
    "Category",
    "Total Budget",
    "Committed",
    "Approved Unpaid",
    "Approved Paid",
    "Remaining",
  ]
  const lines = [header.join(",")]
  rows.forEach((r) => {
    lines.push(
      [
        r.categoryName,
        r.totalBudget,
        r.committed,
        r.approvedUnpaid,
        r.approvedPaid,
        r.remaining,
      ].join(",")
    )
  })
  const blob = new Blob([lines.join("\n")], { type: "text/csv" })
  const url = URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = "procurement-report.csv"
  a.click()
  URL.revokeObjectURL(url)
}

// "View details" link that jumps to the matching focused tab — local
// navigation only, no route change, no new data fetch.
const ViewDetailsLink = ({ onClick }) => (
  <MDButton variant="text" color="info" size="small" onClick={onClick} sx={{ px: 1 }}>
    View details
    <Icon fontSize="small" sx={{ ml: 0.25 }}>
      arrow_forward
    </Icon>
  </MDButton>
)

const NotAvailableNote = ({ icon, text }) => (
  <MDBox display="flex" flexDirection="column" alignItems="center" textAlign="center" py={4} gap={1}>
    <Icon fontSize="large" sx={{ color: "grey.400" }}>
      {icon}
    </Icon>
    <MDTypography variant="body2" color="text" sx={{ maxWidth: 420 }}>
      {text}
    </MDTypography>
  </MDBox>
)

const ReportContainer = () => {
  const [tab, setTab] = useState("overview")
  const { filters, setFilters, resetFilters } = useProcurementFilters(FILTER_DEFAULTS)
  const { data, isFetching, isError, refetch } = useGetProcurementReportQuery(filters)

  const report = data || {}
  const savings = report.savings || {}
  const pending = report.pendingApprovals || {}
  const categories = report.spendByCategory || []
  const regions = report.spendByRegion || []
  const centers = report.spendByCenter || []
  const suppliers = report.topSuppliers || []
  const supplierPerformance = report.supplierPerformance || []
  const budgetVsActual = report.budgetVsActual || []

  const benchmark = Number(savings.benchmark) || 0
  const actual = Number(savings.actual) || 0
  const actualVsBenchmarkPct = benchmark > 0 ? Math.min(100, (actual / benchmark) * 100) : 0

  return (
    <DashboardLayout>
      <MDBox p={3}>
        <ReportHeader
          title="Procurement Report"
          subtitle="Overview of spending, savings, and supplier performance"
          onExport={() => downloadCsv(budgetVsActual)}
          exportDisabled={!budgetVsActual.length}
        />

        <ReportFilterToolbar values={filters} onChange={setFilters} onReset={resetFilters} />

        <ReportTabs tabs={TABS} active={tab} onChange={setTab} />

        {isFetching ? (
          <MDBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={10} gap={1}>
            <Icon fontSize="large" sx={{ color: "grey.400" }}>
              hourglass_top
            </Icon>
            <MDTypography variant="body2" color="text">
              Loading report…
            </MDTypography>
          </MDBox>
        ) : isError ? (
          <MDBox display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={10} gap={1.5}>
            <Icon fontSize="large" color="error">
              error_outline
            </Icon>
            <MDTypography variant="body2" color="text">
              Something went wrong loading the report.
            </MDTypography>
            <MDButton variant="outlined" color="secondary" size="small" onClick={refetch}>
              Retry
            </MDButton>
          </MDBox>
        ) : (
          <>
            {tab === "overview" && (
              <>
                <Grid container spacing={2} mb={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <ReportKpiCard
                      label="Pending Approvals"
                      value={pending.count ?? 0}
                      subtext="requests awaiting approval"
                      icon="pending_actions"
                      color="warning"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <ReportKpiCard
                      label="Avg Cycle Time"
                      value={`${pending.avgCycleDays ?? 0} days`}
                      subtext="average approval turnaround"
                      icon="schedule"
                      color="info"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <ReportKpiCard
                      label="Savings vs Benchmark"
                      value={`${savings.savingsPct ?? 0}%`}
                      subtext={`Benchmark ${formatVND(savings.benchmark)}`}
                      icon="savings"
                      color="success"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <ReportKpiCard
                      label="Actual Spend"
                      value={formatVND(savings.actual)}
                      subtext="Total spend, current filters"
                      icon="payments"
                      color="dark"
                    />
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={7}>
                    <ReportCard
                      title="Spend by Category"
                      subtitle={topContributorSummary(categories)}
                      icon="category"
                      color="info"
                      action={<ViewDetailsLink onClick={() => setTab("category")} />}
                    >
                      <ReportBarList items={categories} color="info" limit={6} />
                    </ReportCard>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <ReportCard
                      title="Spend by Region"
                      subtitle={topContributorSummary(regions)}
                      icon="public"
                      color="success"
                      action={<ViewDetailsLink onClick={() => setTab("region")} />}
                    >
                      <ReportBarList items={regions} color="success" />
                    </ReportCard>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <ReportCard
                      title="Spend by Center"
                      subtitle={topContributorSummary(centers)}
                      icon="apartment"
                      color="warning"
                      action={<ViewDetailsLink onClick={() => setTab("center")} />}
                    >
                      <ReportBarList items={centers} color="warning" limit={6} />
                    </ReportCard>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ReportCard
                      title="Top Suppliers"
                      subtitle="Pareto — spend concentration"
                      icon="leaderboard"
                      color="info"
                      action={<ViewDetailsLink onClick={() => setTab("supplier")} />}
                    >
                      <ReportBarList items={suppliers} color="info" limit={6} />
                    </ReportCard>
                  </Grid>

                  <Grid item xs={12} md={7}>
                    <ReportCard
                      title="Supplier Performance"
                      icon="grade"
                      color="success"
                      action={<ViewDetailsLink onClick={() => setTab("supplier")} />}
                    >
                      <ReportBarList items={supplierPerformance} color="success" money={false} limit={6} />
                    </ReportCard>
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <ReportCard title="Savings vs Benchmark" icon="trending_down" color="success">
                      <MDBox display="flex" flexWrap="wrap" gap={3} mb={2}>
                        <MDBox>
                          <MDTypography
                            variant="caption"
                            color="text"
                            fontWeight="medium"
                            textTransform="uppercase"
                            sx={{ letterSpacing: "0.04em" }}
                          >
                            Benchmark
                          </MDTypography>
                          <MDTypography variant="h6" fontWeight="bold" sx={{ mt: 0.25 }}>
                            {formatVND(savings.benchmark)}
                          </MDTypography>
                        </MDBox>
                        <MDBox>
                          <MDTypography
                            variant="caption"
                            color="text"
                            fontWeight="medium"
                            textTransform="uppercase"
                            sx={{ letterSpacing: "0.04em" }}
                          >
                            Actual
                          </MDTypography>
                          <MDTypography variant="h6" fontWeight="bold" color="success" sx={{ mt: 0.25 }}>
                            {formatVND(savings.actual)}
                          </MDTypography>
                        </MDBox>
                        <MDBox>
                          <MDTypography
                            variant="caption"
                            color="text"
                            fontWeight="medium"
                            textTransform="uppercase"
                            sx={{ letterSpacing: "0.04em" }}
                          >
                            Savings
                          </MDTypography>
                          <MDTypography variant="h6" fontWeight="bold" color="success" sx={{ mt: 0.25 }}>
                            {savings.savingsPct ?? 0}%
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                      <MDBox width="100%" height="0.5rem" borderRadius="md" bgColor="grey-100" overflow="hidden">
                        <MDBox width={`${actualVsBenchmarkPct}%`} height="100%" bgColor="success" borderRadius="md" />
                      </MDBox>
                      <MDBox display="flex" justifyContent="space-between" mt={0.75}>
                        <MDTypography variant="caption" color="text">
                          Actual
                        </MDTypography>
                        <MDTypography variant="caption" color="text">
                          Benchmark
                        </MDTypography>
                      </MDBox>
                    </ReportCard>
                  </Grid>
                </Grid>
              </>
            )}

            {tab === "category" && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <ReportCard
                    title="Category Ranking & Distribution"
                    subtitle={topContributorSummary(categories)}
                    icon="category"
                    color="info"
                  >
                    <ReportBarList items={sortByValueDesc(categories)} color="info" showRank showPercent />
                  </ReportCard>
                </Grid>
                <Grid item xs={12}>
                  <ReportCard title="Budget vs Actual per Category" icon="bar_chart" color="warning">
                    {budgetVsActual.length === 0 ? (
                      <MDTypography variant="body2" color="text">
                        No data for the current filters.
                      </MDTypography>
                    ) : (
                      budgetVsActual.map((row, idx) => (
                        <MDBox
                          key={row.categoryName}
                          mb={idx < budgetVsActual.length - 1 ? 2.5 : 0}
                        >
                          <MDBox display="flex" justifyContent="space-between" mb={0.5}>
                            <MDTypography variant="button" fontWeight="medium">
                              {row.categoryName}
                            </MDTypography>
                            <MDTypography variant="caption" color="text">
                              Budget {formatAmount(row.totalBudget)}
                            </MDTypography>
                          </MDBox>
                          <BulletBar row={row} />
                        </MDBox>
                      ))
                    )}
                  </ReportCard>
                </Grid>
              </Grid>
            )}

            {tab === "region" && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <ReportCard
                    title="Region Ranking & Distribution"
                    subtitle={topContributorSummary(regions)}
                    icon="public"
                    color="success"
                  >
                    <ReportBarList items={sortByValueDesc(regions)} color="success" showRank showPercent />
                  </ReportCard>
                </Grid>
              </Grid>
            )}

            {tab === "center" && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <ReportCard
                    title="Center Ranking & Distribution"
                    subtitle={topContributorSummary(centers)}
                    icon="apartment"
                    color="warning"
                  >
                    <ReportBarList items={sortByValueDesc(centers)} color="warning" showRank showPercent />
                  </ReportCard>
                </Grid>
              </Grid>
            )}

            {tab === "department" && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <ReportCard title="Spend by Department" icon="groups" color="dark">
                    <NotAvailableNote
                      icon="groups"
                      text="Department-level spend isn't available from the current report data yet. Use the Department filter above to scope the other views by department."
                    />
                  </ReportCard>
                </Grid>
              </Grid>
            )}

            {tab === "supplier" && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <ReportCard
                    title="Top Suppliers"
                    subtitle="Pareto — spend concentration"
                    icon="leaderboard"
                    color="info"
                  >
                    <ReportBarList items={sortByValueDesc(suppliers)} color="info" showRank showPercent />
                  </ReportCard>
                </Grid>
                <Grid item xs={12} md={6}>
                  <ReportCard title="Supplier Performance" icon="grade" color="success">
                    <ReportBarList
                      items={sortByValueDesc(supplierPerformance)}
                      color="success"
                      money={false}
                      showRank
                    />
                  </ReportCard>
                </Grid>
              </Grid>
            )}

            {tab === "time" && (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <ReportCard title="Spend Over Time" icon="schedule" color="info">
                    <NotAvailableNote
                      icon="schedule"
                      text={
                        filters.from || filters.to
                          ? `Time-series spend data isn't available from the current report yet for ${
                              filters.from || "…"
                            } – ${filters.to || "…"}.`
                          : "Time-series spend data isn't available from the current report yet. Use the Date Range filter above once trend data is supported."
                      }
                    />
                  </ReportCard>
                </Grid>
              </Grid>
            )}
          </>
        )}
      </MDBox>
    </DashboardLayout>
  )
}

export default ReportContainer
